<template>
    <header
        v-if="show"
        ref="el"
        class="fdidabkb"
        :class="{ thin: thin_, tabs: !!tabs?.length }"
        :style="{ background: bg }"
        @click="onClick"
    >
        <div class="left">
            <div class="buttons">
                <button
                    v-if="displayBackButton"
                    class="_buttonIcon button icon backButton"
                    @click.stop="mainRouter.back()"
                    @touchstart="preventDrag"
                    v-tooltip.bottom.noDelay="$t('goBack')"
                >
                    <mg-icon icon="caret-left" />
                </button>
                <MagAvatar
                    v-if="
                        layout === 'narrow' &&
                        props.displayMyAvatar &&
                        isLoggedIn
                    "
                    class="avatar button"
                    :user="currentUser"
                    :disable-preview="true"
                    disable-link
                    @click.stop="openAccountMenu"
                />
            </div>
            <div
                v-if="!hideTitle && metadata"
                class="titleContainer"
                @click="showTabsPopup"
            >
                <MagAvatar
                    v-if="metadata.avatar"
                    class="avatar"
                    :user="metadata.avatar"
                    :show-indicator="true"
                />
                <mg-icon
                    v-else-if="metadata.icon && layout === 'normal'"
                    class="icon"
                    :icon="metadata.icon"
                    marginEnd="true"
                />

                <div class="title">
                    <MkUserName
                        v-if="metadata.userName"
                        :user="metadata.userName"
                        :nowrap="true"
                        class="title"
                    />
                    <div
                        v-else-if="
                            metadata.title &&
                            !(tabs?.length && layout === 'narrow')
                        "
                        class="title"
                    >
                        {{ metadata.title }}
                    </div>
                    <div
                        v-if="layout === 'normal' && metadata.subtitle"
                        class="subtitle"
                    >
                        {{ metadata.subtitle }}
                    </div>
                </div>
            </div>
        </div>
        <template v-if="metadata">
            <nav
                ref="tabsEl"
                v-if="hasTabs"
                class="tabs"
                :class="{ collapse: hasTabs && tabs?.length > 3 }"
            >
                <button
                    v-for="tab of tabs"
                    ref="tabButton"
                    :key="tab.key"
                    class="tab _button"
                    :class="{
                        active: tab.key === currentTab,
                        animated: animation,
                    }"
                    @click="(ev) => onTabClick(tab, ev)"
                >
                    <mg-icon v-if="tab.icon" class="icon" :icon="tab.icon" />
                    <span class="title">{{ tab.title }}</span>
                </button>
                <div
                    ref="tabHighlightEl"
                    class="highlight"
                    :aria-hidden="true"
                    :class="{ animated: animation }"
                ></div>
            </nav>
        </template>
        <div class="buttons right">
            <template v-if="metadata?.avatar">
                <MkFollowButton
                    :user="metadata.avatar"
                    :full="layout === 'normal'"
                    class="fullButton"
                />
            </template>
            <template v-for="action in actions">
                <button
                    v-tooltip.bottom.noDelay="action.text"
                    class="_buttonIcon button"
                    :class="{ highlighted: action.highlighted }"
                    @click.stop="action.handler"
                    @touchstart="preventDrag"
                >
                    <mg-icon :icon="action.icon" />
                </button>
            </template>
        </div>
    </header>
</template>

<script lang="ts" setup>
import {
    computed,
    inject,
    nextTick,
    onUpdated,
    onWatcherCleanup,
    ref,
    shallowRef,
    useTemplateRef,
    watchEffect,
} from "vue";
import MkFollowButton from "@/components/MkFollowButton.vue";
import { scrollToTop } from "@/scripts/scroll";
import { usePageMetadata } from "@/composables/page-metadata";
import { useFluent } from "fluent-vue";
import { useParentElement } from "@vueuse/core";
import { mainRouter } from "@/router";
import { useSizeBreakpoint } from "@/composables/mag-layout-composable";
import { ShallowRef } from "@vue/runtime-core";
import { useAccountStore } from "@/model/account";
import { storeToRefs } from "pinia";
import {
    ContextMenuAccountSwitcher,
    ContextMenuDynamic,
    magContextMenu,
} from "@/components/context-menu/mag-context-menu";
import { MenuItem } from "@/components/context-menu/mag-dynamic-menu";
import { useSettingsStore } from "@/store/settings-store";
import { refProp } from "@/composables/reactive";

const { $t } = useFluent();
const accountStore = useAccountStore();
const { currentUser, isLoggedIn } = storeToRefs(accountStore);

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const animation = refProp(settings, "animation");

type Tab = {
    key: string;
    title: string;
    icon?: string;
    iconOnly?: boolean;
    onClick?: (ev: MouseEvent) => void;
};

const currentTab = defineModel<string>("tab");

const props = defineProps<{
    tabs?: Tab[];
    actions?: {
        text: string;
        icon: string;
        handler: (ev: MouseEvent) => void;
    }[];
    thin?: boolean;
    displayMyAvatar?: boolean;
    displayBackButton?: boolean;
    to?: string;
}>();

const displayBackButton =
    props.displayBackButton &&
    history.length > 1 &&
    inject("shouldBackButton", true);

const metadata = usePageMetadata();

const hideTitle = inject("shouldOmitHeaderTitle", false);
const thin_ = props.thin || inject("shouldHeaderThin", false);

const el = useTemplateRef("el");
const rootParent = useParentElement(el) as Readonly<
    ShallowRef<HTMLElement | null | undefined>
>;
const tabRefs = useTemplateRef("tabButton");
const tabHighlightEl = useTemplateRef("tabHighlightEl");
const tabsEl = useTemplateRef("tabsEl");
const tabIdx = ref(-1);
const currentTabEl = shallowRef<HTMLButtonElement | null>(null);

watchEffect(() => {
    if (!props.tabs || !tabRefs.value || !currentTab.value) {
        return;
    }

    const idx = props.tabs.findIndex((t) => t.key === currentTab.value);

    if (idx === -1) {
        return;
    }

    nextTick(() => {
        tabIdx.value = idx;
        currentTabEl.value = tabRefs.value[idx] ?? null;
    });
});

const bg = ref(null);
const hasTabs = computed(() => Boolean(props.tabs?.length));
const hasActions = computed(() => Boolean(props.actions?.length));
const show = computed(() => {
    return !hideTitle || hasTabs.value || hasActions.value;
});

const layout = useSizeBreakpoint(() => rootParent.value ?? null, "width", [
    [0, "narrow"],
    [500, "normal"],
]);

const openAccountMenu = (ev: MouseEvent) => {
    magContextMenu({}, ContextMenuAccountSwitcher, {
        parent: ev.target as HTMLElement | null | undefined,
    });
};

const showTabsPopup = (ev: MouseEvent) => {
    if (!hasTabs.value) return;
    if (layout.value === "normal") return;

    ev.preventDefault();
    ev.stopPropagation();
    const menu = props.tabs!.map((tab) => ({
        type: "button" as const,
        props: {
            icon: tab.icon,
            active: tab.key === currentTab.value,
        },
        text: tab.title,
        action: (ev) => {
            onTabClick(tab, ev);
        },
    }));

    magContextMenu(
        {
            items: menu satisfies MenuItem[],
        },
        ContextMenuDynamic,
        {
            parent: (ev.currentTarget ?? ev.target) as
                | HTMLElement
                | null
                | undefined,
        },
    );
};

const preventDrag = (ev: TouchEvent) => {
    ev.stopPropagation();
};

const onClick = (e: MouseEvent) => {
    if (props.to) {
        location.href = props.to;
    } else if (el.value) {
        scrollToTop(el.value, { behavior: "smooth" });
    }
};

function onTabClick(tab: Tab, ev: MouseEvent): void {
    if (tab.onClick) {
        ev.preventDefault();
        ev.stopPropagation();
        tab.onClick(ev);
    }

    currentTab.value = tab.key;
}

const resizeTab = () => {
    const tabEl = currentTabEl.value;
    const tabsElem = tabsEl.value;
    if (!props.tabs || !tabsElem || !tabEl) {
        return;
    }

    const frameId = requestAnimationFrame(() => {
        const tabSizeX =
            tabEl.scrollWidth + tabEl.offsetWidth - tabEl.clientWidth;
        if (props.tabs?.length ?? 0 > 3) {
            tabEl.style = `--width: ${tabSizeX}px`;
        }

        tabsElem.scrollTo({
            left: tabEl.offsetLeft - 60,
            behavior: "smooth",
        });
    });

    onWatcherCleanup(() => {
        cancelAnimationFrame(frameId);
    });
};

watchEffect(resizeTab, {
    flush: "post",
});

onUpdated(() => {
    requestAnimationFrame(resizeTab);
});
</script>

<style lang="scss" scoped>
.fdidabkb {
    --height: 55px;
    display: flex;
    justify-content: space-between;
    width: 100%;
    height: var(--height);
    padding-inline: 24px;
    box-sizing: border-box;
    overflow: hidden;
    @media (max-width: 500px) {
        padding-inline: 16px;
        &.tabs > .buttons > :deep(.follow-button > span) {
            display: none;
        }
    }
    @media (max-width: 700px) {
        > .left {
            min-width: unset !important;
            max-width: 40%;
        }
        > .left,
        > .right {
            flex: unset !important;
        }
        &:not(.tabs) {
            > .left {
                width: 0 !important;
                flex-grow: 1 !important;
                max-width: unset !important;
            }
        }
        &.tabs {
            > .left {
                flex-shrink: 0 !important;
            }

            .buttons ~ .titleContainer > .title {
                display: none;
            }
        }
    }

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        border-bottom: solid 0.5px var(--divider);
        -webkit-backdrop-filter: var(--blur, blur(15px));
        backdrop-filter: var(--blur, blur(15px));
        z-index: -1;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: var(--bg);
        opacity: 0.85;
        z-index: -2;
    }

    &.thin {
        --height: 45px;

        .buttons {
            > .button {
                font-size: 0.9em;
            }
        }
    }

    > .left {
        display: flex;

        > .buttons {
            &:not(:empty) {
                margin-right: 8px;
                margin-left: calc(0px - var(--margin));
            }

            > .avatar {
                width: 32px;
                height: 32px;
                margin-left: var(--margin);
            }
        }
    }

    .buttons {
        --margin: 8px;
        display: flex;
        align-items: center;
        height: var(--height);

        &.right {
            justify-content: flex-end;
            z-index: 2;
            // margin-right: calc(0px - var(--margin));
            // margin-left: var(--margin);
            > .button:last-child {
                margin-right: calc(0px - var(--margin));
            }
        }

        > .fullButton {
            & + .fullButton {
                margin-left: 12px;
            }
        }
    }

    > .left {
        > .backButton {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        > .titleContainer {
            display: flex;
            align-items: center;
            max-width: 400px;
            overflow: auto;
            white-space: nowrap;
            text-align: left;
            font-weight: bold;
            flex-shrink: 0;

            > .avatar {
                $size: 32px;
                display: inline-block;
                width: $size;
                height: $size;
                margin-right: 8px;
            }

            > .icon {
                margin-right: 4px;
            }

            > .title {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                line-height: 1.15;
                padding: 0.15em 0;

                &:not(:has(.subtitle)) {
                    padding-top: 0.5em;
                }

                > .subtitle {
                    opacity: 0.6;
                    font-size: 0.8em;
                    font-weight: normal;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;

                    &.activeTab {
                        text-align: center;

                        > .chevron {
                            display: inline-block;
                            margin-left: 6px;
                        }
                    }
                }
            }
        }
    }

    > .left,
    > .right {
        flex-basis: 100%;
        flex-shrink: 9999;
        overflow: hidden;
    }

    > .left {
        min-width: 20%;
        margin-left: -10px;
        padding-left: 10px;
    }

    > .right {
        // margin-left: auto;
        min-width: max-content;
        margin-right: -10px;
        padding-right: 10px;
    }

    > .tabs {
        justify-content: center;
        contain: strict;
        flex: 1 1 100%;
        position: relative;
        font-size: 1em;
        overflow-x: auto;
        white-space: nowrap;
        display: flex;
        padding-inline: 20px;
        margin: 0 10px;
        mask: linear-gradient(
            to right,
            transparent,
            black 20px calc(100% - 20px),
            transparent
        );
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }

        &.collapse {
            --width: 2.7em;
            // --width: 1.33333em
            > .tab {
                width: 2.7em;
                min-width: 2.7em !important;

                &:not(.active) > .title {
                    opacity: 0;
                }
            }
        }

        &:not(.collapse) > .tab {
            --width: max-content !important;
        }

        > .tab {
            display: inline-flex;
            align-items: center;
            position: relative;
            border-inline: 10px solid transparent;
            height: 100%;
            min-width: max-content;
            font-weight: normal;
            opacity: 0.7;
            overflow: hidden;
            --width: 38px;
            gap: 8px;

            &:hover {
                opacity: 1;
            }

            &.animated {
                transition:
                    color 0.2s,
                    opacity 0.2s,
                    width 0.2s cubic-bezier(0.33, 1, 0.68, 1),
                    min-width 0.2s cubic-bezier(0.33, 1, 0.68, 1),
                    background-color 0.2s cubic-bezier(0.33, 1, 0.68, 1);
            }

            &.active {
                opacity: 1;
                color: var(--accent);
                font-weight: 600;
                width: var(--width);
                min-width: var(--width) !important;
                background-color: var(--buttonBg);
                anchor-name: --active-tab;
            }

            > .title {
                display: inline-block;
                height: 1.333em;
                transition: opacity 0.2s;
            }
        }

        > .highlight {
            position: absolute;
            position-anchor: --active-tab;
            left: anchor(left);
            width: anchor-size();
            bottom: 0;

            height: 3px;
            background: var(--accent);
            border-radius: 999px;
            pointer-events: none;

            &.animated {
                transition:
                    width 0.3s cubic-bezier(0.22, 0.61, 0.36, 1),
                    left 0.2s cubic-bezier(0.22, 0.61, 0.36, 1);
            }
        }
    }
}
</style>
